import { jsx as _jsx } from "react/jsx-runtime";
import { ComponentSync, StorageKey } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_DiseaseProfile } from '@app/dp-frontend';
import { asArray } from '@nu-art/ts-common';
import { ModuleFE_VolatileImport } from '../ModuleFE_VolatileImport.js';
import { Component_ResizableReviewPanel } from '../Component_ResizableReviewPanel/Component_ResizableReviewPanel.js';
// KM-local width preference for the DP review overlay (px). Distinct key from the pathway review.
const StorageKey_DPReviewWidth = new StorageKey('disease-profile-review-html__width');
/**
 * Renders the optional disease-profile HTML into the `disease-profile-html` slot, docked as an overlay
 * next to the DP editor. Shown only while the review is open (toggled via the "Review" section link or
 * this panel's X). Shows the saved Storage URL when the DP has one; otherwise, on a volatile preview,
 * shows the locally-attached HTML File via an object URL (revoked on unmount). The iframe is fully
 * sandboxed (sandbox="") with no referrer so the download token never leaks via the Referer header.
 * Mirrors Component_PathwayHtml.
 */
export class Component_DiseaseProfileHtml extends ComponentSync {
    __onDiseaseProfileReviewToggled = (reviewKey) => {
        if (reviewKey === `${this.props.tabId}::${this.props.diseaseProfileId}`)
            this.reDeriveState();
    };
    // Re-derive when this DP's cache entry changes (e.g. HTML attached/replaced/removed in edit mode)
    // so the iframe src reflects the new diseaseProfileHtmlUrl without needing to toggle the overlay.
    __onDiseaseProfileUpdated = (...params) => {
        if (asArray(params[1]).every(dp => dp._id !== this.props.diseaseProfileId))
            return;
        this.reDeriveState();
    };
    deriveStateFromProps(nextProps, state) {
        state.isOpen = ModuleFE_VolatileImport.isDiseaseProfileReviewOpen(nextProps.tabId, nextProps.diseaseProfileId);
        const diseaseProfile = ModuleFE_DiseaseProfile.cache.unique(nextProps.diseaseProfileId);
        state.diseaseProfileLabel = diseaseProfile?.label ?? '';
        const savedUrl = diseaseProfile?.diseaseProfileHtmlUrl;
        if (savedUrl) {
            this.revoke(state.objectUrl); // drop any local object URL we were holding
            state.src = savedUrl;
            state.objectUrl = undefined;
            return state;
        }
        // Not saved yet: show the local file the user attached to this preview, if any.
        const importId = ModuleFE_VolatileImport.getImportIdForDiseaseProfile(nextProps.tabId, nextProps.diseaseProfileId);
        const file = importId ? ModuleFE_VolatileImport.getPendingDiseaseProfileHtmlFile(importId) : undefined;
        if (!file) {
            this.revoke(state.objectUrl);
            state.src = undefined;
            state.objectUrl = undefined;
            state.pendingFile = undefined;
            return state;
        }
        // Reuse the object URL across re-renders, but recreate it if the attached File changed (e.g. the
        // user replaced the HTML in the dialog and re-imported without the slot remounting).
        if (state.objectUrl && state.pendingFile === file)
            return state;
        this.revoke(state.objectUrl);
        const objectUrl = URL.createObjectURL(file);
        state.src = objectUrl;
        state.objectUrl = objectUrl;
        state.pendingFile = file;
        return state;
    }
    componentWillUnmount() {
        this.revoke(this.state.objectUrl);
    }
    // Prototype method (not an instance arrow field): deriveStateFromProps runs inside the base
    // constructor, before instance fields are assigned, so an arrow field here would be undefined.
    revoke(url) {
        if (url)
            URL.revokeObjectURL(url);
    }
    render() {
        if (!this.state.src || !this.state.isOpen)
            return null;
        return _jsx(Component_ResizableReviewPanel, { storageKey: StorageKey_DPReviewWidth, title: 'Review', label: this.state.diseaseProfileLabel, src: this.state.src, onClose: () => ModuleFE_VolatileImport.closeDiseaseProfileReview(this.props.tabId, this.props.diseaseProfileId) });
    }
}
//# sourceMappingURL=Component_DiseaseProfileHtml.js.map